import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";

type FitMode = "true-size" | "cover" | "contain";

interface GridConfig {
  columns: number;
  rows: number;
  gap: number;
  backgroundColor: string;
  fit: FitMode;
}

interface SourceImage {
  blob: Blob;
  url: string;
  name: string;
  width?: number;
  height?: number;
}

interface GridConfigModalProps {
  imageCount: number;
  images: SourceImage[];
  config: GridConfig;
  onSave: (config: GridConfig) => void;
  onCancel: () => void;
}

// Calculate optimal grid dimensions for a given image count
const getOptimalGrid = (count: number): { columns: number; rows: number } => {
  if (count <= 1) return { columns: 1, rows: 1 };
  if (count === 2) return { columns: 2, rows: 1 };
  if (count === 3) return { columns: 3, rows: 1 };
  if (count === 4) return { columns: 2, rows: 2 };

  // Find the most square-like arrangement
  const sqrt = Math.sqrt(count);
  let cols = Math.ceil(sqrt);
  let rows = Math.ceil(count / cols);

  // Prefer wider grids for landscape feel
  if (rows > cols) {
    [cols, rows] = [rows, cols];
  }

  return { columns: cols, rows };
};

// Get all valid grid options for a given image count
const getGridOptions = (count: number): Array<{ columns: number; rows: number }> => {
  const options: Array<{ columns: number; rows: number }> = [];

  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    // Only add if this configuration makes sense
    if (cols * rows >= count && cols * (rows - 1) < count) {
      options.push({ columns: cols, rows });
    }
  }

  return options;
};

const GridConfigModal: React.FC<GridConfigModalProps> = ({
  imageCount,
  images,
  config,
  onSave,
  onCancel,
}) => {
  const [localConfig, setLocalConfig] = useState<GridConfig>({
    ...config,
    fit: config.fit || "true-size",
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  const gridOptions = useMemo(() => getGridOptions(imageCount), [imageCount]);

  // Update local config when image count changes
  useEffect(() => {
    const optimal = getOptimalGrid(imageCount);
    setLocalConfig((prev) => ({
      ...prev,
      columns: optimal.columns,
      rows: optimal.rows,
    }));
  }, [imageCount]);

  // Generate actual preview image
  const generatePreview = useCallback(async (cfg: GridConfig) => {
    if (images.length < 2) return;

    setGenerating(true);

    try {
      // Load all images as bitmaps
      const bitmaps = await Promise.all(
        images.map((src) => createImageBitmap(src.blob))
      );

      // Find max dimensions per cell for uniform sizing
      const maxWidth = Math.max(...bitmaps.map((b) => b.width));
      const maxHeight = Math.max(...bitmaps.map((b) => b.height));

      // Calculate canvas size
      const totalWidth = cfg.columns * maxWidth + (cfg.columns - 1) * cfg.gap;
      const totalHeight = cfg.rows * maxHeight + (cfg.rows - 1) * cfg.gap;

      // Scale down for preview (max 600px wide)
      const scale = Math.min(1, 600 / totalWidth);
      const previewWidth = Math.round(totalWidth * scale);
      const previewHeight = Math.round(totalHeight * scale);

      const canvas = document.createElement("canvas");
      canvas.width = previewWidth;
      canvas.height = previewHeight;
      const ctx = canvas.getContext("2d")!;

      // Fill background
      if (cfg.backgroundColor === "transparent") {
        // Draw checkerboard pattern for transparent background
        const checkSize = 10;
        for (let y = 0; y < previewHeight; y += checkSize) {
          for (let x = 0; x < previewWidth; x += checkSize) {
            ctx.fillStyle = ((x + y) / checkSize) % 2 === 0 ? "#ccc" : "#fff";
            ctx.fillRect(x, y, checkSize, checkSize);
          }
        }
      } else {
        ctx.fillStyle = cfg.backgroundColor;
        ctx.fillRect(0, 0, previewWidth, previewHeight);
      }

      // Draw each image in its grid cell
      bitmaps.forEach((bitmap, index) => {
        if (index >= cfg.columns * cfg.rows) return;

        const col = index % cfg.columns;
        const row = Math.floor(index / cfg.columns);
        const cellX = col * (maxWidth + cfg.gap);
        const cellY = row * (maxHeight + cfg.gap);

        let drawX: number, drawY: number, drawW: number, drawH: number;

        if (cfg.fit === "true-size") {
          // Center image at its true size
          drawW = bitmap.width;
          drawH = bitmap.height;
          drawX = cellX + (maxWidth - drawW) / 2;
          drawY = cellY + (maxHeight - drawH) / 2;
        } else if (cfg.fit === "cover") {
          // Scale to cover the cell, cropping if necessary
          const scaleX = maxWidth / bitmap.width;
          const scaleY = maxHeight / bitmap.height;
          const fitScale = Math.max(scaleX, scaleY);
          drawW = bitmap.width * fitScale;
          drawH = bitmap.height * fitScale;
          drawX = cellX + (maxWidth - drawW) / 2;
          drawY = cellY + (maxHeight - drawH) / 2;
        } else {
          // contain: Scale to fit within cell
          const scaleX = maxWidth / bitmap.width;
          const scaleY = maxHeight / bitmap.height;
          const fitScale = Math.min(scaleX, scaleY);
          drawW = bitmap.width * fitScale;
          drawH = bitmap.height * fitScale;
          drawX = cellX + (maxWidth - drawW) / 2;
          drawY = cellY + (maxHeight - drawH) / 2;
        }

        // Scale coordinates for preview
        ctx.drawImage(
          bitmap,
          drawX * scale,
          drawY * scale,
          drawW * scale,
          drawH * scale
        );
      });

      // Close bitmaps
      bitmaps.forEach((b) => b.close());

      // Convert canvas to blob URL
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error("Failed to create preview"));
        }, "image/jpeg", 0.85);
      });

      // Revoke old preview URL
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }

      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      console.error("Failed to generate preview:", err);
    } finally {
      setGenerating(false);
    }
  }, [images]);

  // Generate preview when config changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      generatePreview(localConfig);
    }, 100); // Small debounce

    return () => clearTimeout(timeout);
  }, [localConfig, generatePreview]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const handlePresetClick = (cols: number, rows: number) => {
    setLocalConfig((prev) => ({ ...prev, columns: cols, rows }));
  };

  return (
    <div className="grid-config-overlay" onClick={onCancel}>
      <div className="grid-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="grid-config-header">
          <h3>Grid Configuration</h3>
          <button className="grid-config-close-btn" onClick={onCancel}>
            &times;
          </button>
        </div>

        <div className="grid-config-content">
          {/* Preview at top, no label */}
          <div className="grid-config-section">
            <div className="grid-preview-container">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Grid preview"
                  className={`grid-preview-image ${generating ? "generating" : ""}`}
                />
              ) : (
                <div className="grid-preview-placeholder">
                  {generating ? "Generating..." : "Loading preview..."}
                </div>
              )}
              {generating && <div className="grid-preview-spinner" />}
            </div>
            <span className="grid-preview-info">
              {imageCount} image{imageCount !== 1 ? "s" : ""} in {localConfig.columns}×{localConfig.rows} grid
              {localConfig.columns * localConfig.rows > imageCount && (
                <> ({localConfig.columns * localConfig.rows - imageCount} empty)</>
              )}
            </span>
          </div>

          {/* Layout presets */}
          <div className="grid-config-section">
            <label className="grid-config-label">Layout</label>
            <div className="grid-presets">
              {gridOptions.slice(0, 6).map(({ columns, rows }) => (
                <button
                  key={`${columns}x${rows}`}
                  className={`grid-preset-btn ${
                    localConfig.columns === columns && localConfig.rows === rows
                      ? "active"
                      : ""
                  }`}
                  onClick={() => handlePresetClick(columns, rows)}
                >
                  {columns}×{rows}
                </button>
              ))}
            </div>
          </div>

          {/* Fit control */}
          <div className="grid-config-section">
            <label className="grid-config-label">Fit</label>
            <div className="grid-presets">
              {([
                { value: "true-size", label: "True size" },
                { value: "cover", label: "Cover" },
                { value: "contain", label: "Contain" },
              ] as const).map(({ value, label }) => (
                <button
                  key={value}
                  className={`grid-preset-btn ${localConfig.fit === value ? "active" : ""}`}
                  onClick={() => setLocalConfig((prev) => ({ ...prev, fit: value }))}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Gap slider */}
          <div className="grid-config-section">
            <label className="grid-config-label">Gap (px)</label>
            <div className="grid-config-slider-row">
              <input
                type="range"
                min="0"
                max="50"
                value={localConfig.gap}
                onChange={(e) =>
                  setLocalConfig((prev) => ({ ...prev, gap: parseInt(e.target.value) }))
                }
                className="grid-config-slider"
              />
              <span className="grid-config-value">{localConfig.gap}px</span>
            </div>
          </div>

          {/* Background - simplified options */}
          <div className="grid-config-section">
            <label className="grid-config-label">Background</label>
            <div className="grid-config-colors">
              {["transparent", "#ffffff", "#000000"].map((color) => (
                <button
                  key={color}
                  className={`grid-color-btn ${
                    localConfig.backgroundColor === color ? "active" : ""
                  } ${color === "transparent" ? "transparent-bg" : ""}`}
                  style={{ backgroundColor: color === "transparent" ? undefined : color }}
                  onClick={() => setLocalConfig((prev) => ({ ...prev, backgroundColor: color }))}
                  title={color === "transparent" ? "Transparent" : color === "#ffffff" ? "White" : "Black"}
                />
              ))}
              <input
                type="color"
                value={localConfig.backgroundColor === "transparent" ? "#ffffff" : localConfig.backgroundColor}
                onChange={(e) =>
                  setLocalConfig((prev) => ({ ...prev, backgroundColor: e.target.value }))
                }
                className="grid-color-picker"
                title="Custom color"
              />
            </div>
          </div>
        </div>

        <div className="grid-config-actions">
          <button className="grid-config-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="grid-config-save-btn"
            onClick={() => onSave(localConfig)}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default GridConfigModal;
export { getOptimalGrid };
export type { GridConfig, FitMode };
